// Pattern shadcn: il file esporta sia il componente sia le `buttonVariants`
// usate altrove per ottenere le stesse classi. Convivono di proposito,
// quindi sopprimiamo la regola sul fast refresh.
/* eslint-disable react-refresh/only-export-components */
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@v2/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-full font-semibold",
        outline: "bg-raised text-foreground hover:bg-[hsl(var(--active))] rounded-full",
        secondary: "bg-raised text-foreground hover:bg-[hsl(var(--active))] rounded-full",
        ghost: "text-muted-foreground hover:bg-raised hover:text-foreground rounded-full",
        link: "text-foreground underline underline-offset-4 decoration-[hsl(var(--faint))] hover:decoration-current",
        hero: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold",
        gaming: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold",
        gradient: "bg-primary text-primary-foreground hover:bg-primary/90 rounded-full font-semibold",
      },
      size: {
        default: "h-11 px-5",
        sm: "h-9 px-4",
        lg: "h-12 px-7 text-[15px]",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
